import React, { useMemo } from "react";
import { FiX } from "react-icons/fi";

const formatBranchLabel = (branches, branchId) => {
  if (branchId === null || branchId === undefined) return "Global";
  const match = branches.find((branch) => String(branch.id) === String(branchId));
  return match?.branchName || `Sucursal ${branchId}`;
};

const UserDetailsModal = ({
  user,
  branches = [],
  branchUsers = [],
  branchAccessMap = {},
  isGlobalView = false,
  resolvedBranchId,
  onClose,
}) => {
  if (!user) return null;

  const branchUserIds = useMemo(
    () => new Set(branchUsers.map((entry) => String(entry.userId))),
    [branchUsers]
  );

  const roleEntries = (user.Roles || []).map((role) => ({
    id: role.id,
    name: role.name,
    branchId: role.UserRole?.branchId ?? null,
  }));

  const adminRole = roleEntries.find((role) => role.name === "Admin" && role.branchId === null);
  const accessBranches = isGlobalView
    ? Array.from(branchAccessMap[String(user.id)] || [])
    : resolvedBranchId
      ? [String(resolvedBranchId)]
      : [];

  const hasBranchAccess = isGlobalView
    ? accessBranches.length > 0
    : adminRole || branchUserIds.has(String(user.id));

  return (
    <div
      className="fixed inset-0 bg-black/50 flex justify-center items-start z-50 px-4 py-6 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-white w-full max-w-3xl rounded-2xl shadow-2xl border border-gray-100 overflow-hidden max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between px-6 py-4 border-b border-gray-100 bg-gray-50">
          <div>
            <p className="text-xs uppercase tracking-wide text-gray-500">Usuario</p>
            <h2 className="text-lg font-semibold text-gray-900">Detalle de usuario</h2>
            <p className="text-xs text-gray-500 mt-1">
              {user.name || user.username}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-600 hover:text-gray-900 bg-white border border-gray-200 rounded-full w-9 h-9 flex items-center justify-center"
            type="button"
            aria-label="Cerrar"
          >
            <FiX size={18} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <p className="text-xs uppercase tracking-wide text-gray-500">Nombre</p>
              <p className="text-base font-semibold text-gray-900">{user.name || "—"}</p>
              <p className="text-xs text-gray-500 mt-1">{user.username}</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <p className="text-xs uppercase tracking-wide text-gray-500">Contacto</p>
              <p className="text-sm text-gray-700">Email: {user.username || "—"}</p>
              <p className="text-sm text-gray-700">Teléfono: {user.phone || "—"}</p>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
            <div className="flex flex-wrap items-center gap-2 text-xs font-medium">
              <span
                className={`px-2 py-1 rounded ${
                  adminRole
                    ? "bg-indigo-100 text-indigo-700"
                    : hasBranchAccess
                      ? "bg-green-100 text-green-700"
                      : "bg-gray-100 text-gray-600"
                }`}
              >
                {adminRole
                  ? "Acceso total"
                  : hasBranchAccess
                    ? "Acceso en sucursal"
                    : "Sin acceso"}
              </span>
              {adminRole && (
                <span className="px-2 py-1 rounded bg-blue-50 text-blue-700">
                  Admin (global)
                </span>
              )}
            </div>

            <div>
              <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">
                Roles asignados
              </p>
              {roleEntries.length === 0 ? (
                <p className="text-sm text-gray-500">Sin roles asignados.</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {roleEntries.map((role) => (
                    <div key={`${role.id}-${role.branchId ?? "global"}`} className="border border-gray-200 rounded-lg px-3 py-2 text-sm">
                      <p className="font-semibold text-gray-900">{role.name}</p>
                      <p className="text-xs text-gray-500">{formatBranchLabel(branches, role.branchId)}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {isGlobalView && (
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">
                  Acceso a sucursales
                </p>
                {accessBranches.length === 0 ? (
                  <p className="text-sm text-gray-500">Sin acceso a sucursales.</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {accessBranches.map((branchId) => (
                      <span
                        key={branchId}
                        className="px-2 py-1 rounded bg-slate-100 text-slate-700 text-xs"
                      >
                        {formatBranchLabel(branches, branchId)}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserDetailsModal;
